<template>
  <div
    id="vendor-status-card"
    class="tw-bg-[#FCFCFC] tw-h-[119px] tw-w-[100%] tw-border tw-border-solid tw-border-[#EFF0F1] tw-rounded-[6px]"
  >
    <div class="tw-flex tw-h-full tw-justify-between">
      <div class="tw-flex tw-flex-1 tw-p-5">
        <div class="detail-info">
          <div
            class="tw-text-[#211F58] tw-text-[13px] tw-font-[500] tw-leading-[130%] tw-not-italic"
          >
            <div
              class="tw-flex tw-justify-between tw-items-center tw-text-[15px] tw-font-[600] tw-leading-[130%] tw-not-italic"
            >
              {{
                getComponentUiText("app-component-assessment-title-status-1")
              }}
              <div v-if="isShowTag" class="tw-text-[#387DFF] tw-text-[13px]">
                <VendorStatusBadge
                  class="tw-h-[100%] tw-flex tw-leading-[100%] tw-pb-[2px] tw-truncate tw-w-fit"
                  :value="status.statusId"
                />
              </div>
            </div>

            <div
              v-if="props.status.progress"
              class="tw-flex tw-justify-between tw-items-center tw-font-[500] tw-my-[11px]"
            >
              {{ subText1 }}
              <div class="tw-font-[400]">
                <span class="tw-pr-1 tw-text-[#B7B7B7]"
                  >{{ props.status.progress.completed }}/{{
                    props.status.progress.total
                  }}</span
                >
                <span class="tw-text-[#878787]"
                  >{{ props.status.progress.percent }}%</span
                >
              </div>
            </div>
            <div
              v-else
              class="tw-flex tw-justify-between tw-items-center tw-font-[500] tw-my-[11px]"
            >
              {{ subText1 }}
              <div class="tw-text-[#878787] tw-font-[400]">
                {{ getCurrentTime() }}
              </div>
            </div>

            <div
              v-if="props.status.score"
              class="tw-flex tw-justify-between tw-items-center tw-font-[500]"
            >
              {{ subText2 }}
              <div class="tw-font-[400]">
                <span class="tw-pr-1 tw-text-[#B7B7B7]"
                  >{{ props.status.score.completed }}/{{
                    props.status.score.total
                  }}</span
                >
                <span class="tw-text-[#878787]"
                  >{{ props.status.score.percent }}%</span
                >
              </div>
            </div>
            <div
              v-else-if="isShowScore"
              class="tw-flex tw-justify-between tw-items-center tw-font-[500]"
            >
              {{ subText2 }}
              <span class="tw-text-[#878787] tw-font-[400]">
                {{
                  props.status.started
                    ? getStartedTime(props.status.started)
                    : "-"
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="props.status.progress"
        class="tw-px-[5px] sm:tw-p-[20px] tw-border-l tw-border-solid tw-border-[#EFF0F1] tw-h-full tw-border-[0] tw-flex tw-justify-center tw-items-center tw-min-w-[80px]"
      >
        <RadialProgressBar
          class="d-inline"
          :total-steps="100"
          :completed-steps="status.radialValue"
          :is-hide-progress="true"
          outer-stroke-color="#48A0FF"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getComponentUiText } from "@/utils/utilities";
import {
  convertUTCTimeToLocalTime,
  getTimeFromNow,
} from "@/utils/dateTimeUtils";
import StatusBadge from "../common/setting/statusBadge.vue";
import RadialProgressBar from "../prospect/prospectQuestions/componentTypes/radialProgressBar.vue";
import VendorStatusBadge from "./vendorStatusBadge.vue";

const props = defineProps({
  isShowTag: {
    type: Boolean,
    required: false,
    default: true,
  },
  subText1: {
    type: String,
    required: false,
    default: getComponentUiText(
      "app-component-assessments-status-card-created-1",
    ),
  },
  subValue1: {
    type: String,
    required: false,
    default: "",
  },
  subText2: {
    type: String,
    required: false,
    default: getComponentUiText(
      "app-component-assessments-status-card-last-updated-1",
    ),
  },
  isShowScore: {
    type: Boolean,
    required: false,
    default: true,
  },
  status: {
    type: Object,
    required: true,
    default: () => ({
      statusCode: "active",
      statusId: "active",
      radialValue: 0,
      progress: {
        type: Object,
        required: false,
        default: () => ({
          completed: 0,
          total: 0,
          percent: 0,
        }),
      },
      score: {
        type: Object,
        required: false,
        default: () => ({
          completed: 0,
          total: 0,
          percent: 0,
        }),
      },
    }),
  },
});
const getCurrentTime = () => {
  if (!props.status.created) return "-";
  return convertUTCTimeToLocalTime(
    props.status.created,
    undefined,
    "HH:mm",
    "MMMM",
    " - ",
  );
};
const getStartedTime = (time: any) => {
  return getTimeFromNow(time);
};
</script>

<style scoped>
.detail-info {
  flex: 1;
  font-family: Poppins;
}
.default {
  background: var(--ui-white, #fff);
  color: var(--ui-dark-grey, #878787);
  border-radius: 4px;
  border: 1px solid var(--ui-rule, #eff0f1);
}
.inProgress {
  background: linear-gradient(
      0deg,
      rgba(72, 160, 255, 0.15) 0%,
      rgba(72, 160, 255, 0.15) 100%
    ),
    #fff;
  color: #387dff;
}
.notStarted {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}
.complete {
  background: var(--ui-green-soft, #d9ede2);
  color: #01893d;
}
.audited {
  background: linear-gradient(
      0deg,
      rgba(144, 75, 214, 0.15) 0%,
      rgba(144, 75, 214, 0.15) 100%
    ),
    #fff;
  color: #904bd6;
}
.created {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}
</style>
